/**
 * Shared hash computation utilities for consistent hashing across client and server
 * Used by InMemorySourceOfTruth, SQLiteService, and SharedDatabaseUtils
 */

// Simple hash function that works in both Node.js and browser
function createSimpleHash(data: string): string {
  let hash = 0;
  if (data.length === 0) return hash.toString(16);
  
  for (let i = 0; i < data.length; i++) {
    const char = data.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(16).padStart(8, '0');
}

// Try to use crypto if available, fallback to simple hash
function computeHash(data: string): string {
  try {
    // Node.js environment
    if (typeof require !== 'undefined') {
      const crypto = require('crypto');
      return crypto.createHash('sha256').update(data).digest('hex').substring(0, 8);
    }
    
    // Browser environment with Web Crypto API
    if (typeof window !== 'undefined' && window.crypto && window.crypto.subtle) {
      // Note: This would need to be async, but for simplicity using sync fallback
      return createSimpleHash(data);
    }
    
    // Fallback
    return createSimpleHash(data);
  } catch {
    return createSimpleHash(data);
  }
}

export interface PropertyForHashing {
  property: string;
  value: string;
}

/**
 * Compute consistent content hash from properties
 * This is the canonical implementation used across client and server
 */
export function computeContentHash(properties: PropertyForHashing[]): string {
  // Sort properties by name for consistent hashing
  const sortedEntries = properties
    .sort((a, b) => a.property.localeCompare(b.property))
    .map(p => `${p.property}:${typeof p.value === 'string' ? p.value : JSON.stringify(p.value)}`);
  
  const contentString = sortedEntries.join('|');
  return computeHash(contentString);
}

/**
 * Compute subtree hash based ONLY on children's subtree hashes
 * Content hash and subtree hash are independent - subtree hash should not include content
 */
export function computeSubtreeHash(contentHash: string, childSubtreeHashes: string[]): string {
  // For leaf objects (no children), subtree hash should be "0"
  // This ensures objects without children are marked as incomplete subtrees
  if (childSubtreeHashes.length === 0) {
    return "0";
  }
  
  // Compute hash based ONLY on children (no content hash included)
  const sortedChildHashes = childSubtreeHashes.sort();
  const subtreeData = sortedChildHashes.join('|');
  return computeHash(subtreeData);
}
